/**
 * Connection — Connection model (arrow/line)
 * Per PLAN.md models/Connection.js and PRD §4.4
 */

// Connection types per PRD §4.4
export const ConnectionType = {
    ARROW: 'arrow',           // ──────▶
    LINE: 'line',             // ────────
    DOUBLE_ARROW: 'doubleArrow', // ◀──────▶
    DASHED: 'dashed'          // - - - - ▶
};

export class Connection {
    /**
     * Create a new connection
     * @param {string} sourceId - Source node ID
     * @param {string} targetId - Target node ID
     * @param {string} type - Connection type
     */
    constructor(sourceId, targetId, type = ConnectionType.ARROW) {
        this.id = crypto.randomUUID();
        this.sourceId = sourceId;
        this.targetId = targetId;
        this.type = type;
        this.curvature = 0;  // 0 = straight, positive = curve
        this.createdAt = Date.now();
    }

    /**
     * Set connection curvature
     * @param {number} curvature 
     */
    setCurvature(curvature) {
        this.curvature = curvature;
    }

    /**
     * Change connection type
     * @param {string} type 
     */
    setType(type) {
        this.type = type;
    }

    /**
     * Check if connection involves a node
     * @param {string} nodeId 
     * @returns {boolean}
     */
    involvesNode(nodeId) {
        return this.sourceId === nodeId || this.targetId === nodeId;
    }

    /**
     * Clone this connection
     * @returns {Connection}
     */
    clone() {
        const clone = new Connection(this.sourceId, this.targetId, this.type);
        clone.curvature = this.curvature;
        return clone;
    }

    /**
     * Serialize to JSON
     * @returns {Object}
     */
    toJSON() {
        return {
            id: this.id,
            sourceId: this.sourceId,
            targetId: this.targetId,
            type: this.type,
            curvature: this.curvature,
            createdAt: this.createdAt
        };
    }

    /**
     * Create connection from JSON
     * @param {Object} data 
     * @returns {Connection}
     */
    static fromJSON(data) {
        const conn = new Connection(data.sourceId, data.targetId, data.type);
        conn.id = data.id;
        conn.curvature = data.curvature || 0;
        conn.createdAt = data.createdAt || Date.now();
        return conn;
    }
}
